import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { AuthContext } from "../context/AuthContext";
import { WebSocketContext } from "../context/WebSockectContext";
import api from "../context/api";
import "../styles/UserChat.css";

const UserChat = () => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dashAmount, setDashAmount] = useState("");
  const [isMenuVisible, setMenuVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const { ws } = useContext(WebSocketContext);
  const { token, fullName, userBalance, isLoggedIn, phoneNumber, userData } = useContext(AuthContext);
  const chatEndRef = useRef(null);
  const messageRefs = useRef({});
  const modalContentRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const hasScrolledToLastRead = useRef(false);
  const previousMessageCount = useRef(0);

  // Helper to check if message is from current user
  const isMyMessage = useCallback((messageSender) => {
    const myFullNames = [
      fullName?.trim(),
      userData?.fullName?.trim(),
      userData?.name?.trim(),
    ].filter(Boolean);
    
    // Extract first names from all possible full names
    const myFirstNames = myFullNames.map(name => name.split(' ')[0]);
    
    // Check if the message sender matches any of our first names
    return myFirstNames.some(firstName => firstName === messageSender?.trim());
  }, [fullName, userData]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Helper to extract first name only
  const getFirstName = useCallback((fullName) => {
    if (!fullName) return "User";
    return fullName.trim().split(' ')[0];
  }, []);

  // Helper to check if a message is from today
  const isToday = useCallback((timestamp) => {
    const messageDate = new Date(timestamp);
    const today = new Date();
    return messageDate.getDate() === today.getDate() &&
           messageDate.getMonth() === today.getMonth() &&
           messageDate.getFullYear() === today.getFullYear();
  }, []);

  // Helper to format date for separators
  const formatDateSeparator = useCallback((timestamp) => {
    const messageDate = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (isToday(timestamp)) {
      return "Today";
    } else if (
      messageDate.getDate() === yesterday.getDate() &&
      messageDate.getMonth() === yesterday.getMonth() &&
      messageDate.getFullYear() === yesterday.getFullYear()
    ) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      });
    }
  }, [isToday]);

  // Check if we should show date separator
  const shouldShowDateSeparator = useCallback((currentMsg, previousMsg) => {
    if (!previousMsg) return true;
    const currentDate = new Date(currentMsg.timestamp).toDateString();
    const previousDate = new Date(previousMsg.timestamp).toDateString();
    return currentDate !== previousDate;
  }, []);

  // Track scroll position and save the visible message
  useEffect(() => {
    if (!modalOpen || messages.length === 0) return;

    const handleScroll = () => {
      const container = messagesContainerRef.current;
      if (!container || messages.length === 0) return;

      // Find the first message that's visible in the viewport
      // We loop through messages in order to find the one at the top of the view
      for (const msg of messages) {
        const element = messageRefs.current[msg._id];
        if (element) {
          const rect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          // Find the first message whose bottom is visible in the container
          if (rect.bottom > containerRect.top + 20) {
            localStorage.setItem(`lastReadMessage_${fullName || phoneNumber}`, msg._id);
            break;
          }
        }
      }
    };

    const container = messagesContainerRef.current;
    if (container) {
      // Debounce scroll event
      let scrollTimeout;
      const debouncedScroll = () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(handleScroll, 200);
      };
      
      container.addEventListener('scroll', debouncedScroll);
      return () => {
        container.removeEventListener('scroll', debouncedScroll);
        clearTimeout(scrollTimeout);
      };
    }
  }, [modalOpen, messages, fullName, phoneNumber]);

  // Monitor WebSocket connection state
  useEffect(() => {
    if (!ws) {
      setWsConnected(false);
      return;
    }

    const checkConnection = () => {
      setWsConnected(ws.readyState === WebSocket.OPEN);
    };

    checkConnection();
    const interval = setInterval(checkConnection, 1000);

    return () => clearInterval(interval);
  }, [ws]);

  // Auto-scroll to bottom only when new messages arrive (after initial load)
  useEffect(() => {
    // Only auto-scroll if: positioned to last read AND new message added (count increased)
    if (hasScrolledToLastRead.current && messages.length > previousMessageCount.current) {
      const container = messagesContainerRef.current;
      if (container) {
        // If user is already near bottom (within 150px), auto-scroll to the new message
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
        
        // Check if the last message is from the current user
        const lastMessage = messages[messages.length - 1];
        const isFromMe = lastMessage && isMyMessage(lastMessage.sender);
        
        if (isNearBottom || isFromMe) {
          scrollToBottom();
        }
      }
    }
    previousMessageCount.current = messages.length;
  }, [messages, isMyMessage]);

  // Handle mobile keyboard visibility
  useEffect(() => {
    if (!modalOpen) return;

    const handleResize = () => {
      // Scroll to bottom when keyboard appears (viewport height changes)
      if (inputRef.current && document.activeElement === inputRef.current) {
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 100);
      }
    };

    const handleFocus = () => {
      // Scroll input into view when focused on mobile
      if (window.innerWidth <= 768) {
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 300);
      }
    };

    const currentInput = inputRef.current;

    window.addEventListener('resize', handleResize);
    if (currentInput) {
      currentInput.addEventListener('focus', handleFocus);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (currentInput) {
        currentInput.removeEventListener('focus', handleFocus);
      }
    };
  }, [modalOpen]);

  // Fetch messages from the database on component mount
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await api.get("/chat1/:id");
        const data = response.data;
        hasScrolledToLastRead.current = false; // Reset scroll flag before setting messages
        previousMessageCount.current = 0; // Reset message count
        setMessages(data.messages || []);
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    };

    if (isLoggedIn && modalOpen) {
      fetchMessages();
    }
  }, [isLoggedIn, modalOpen]);

  // Scroll to last read message or bottom
  useEffect(() => {
    if (!modalOpen || messages.length === 0 || hasScrolledToLastRead.current) return;

    const performInitialScroll = () => {
      // Re-read from localStorage to get the most up-to-date value
      const storedId = localStorage.getItem(`lastReadMessage_${fullName || phoneNumber}`);
      
      if (storedId) {
        const element = messageRefs.current[storedId];
        if (element) {
          // Scroll to the exact last read message and put it at the top
          element.scrollIntoView({ behavior: 'auto', block: 'start' });
          hasScrolledToLastRead.current = true;
          return;
        }
      }
      
      // If no last read message found or element not found, scroll to bottom
      scrollToBottom();
      hasScrolledToLastRead.current = true;
    };

    // Use a slightly longer timeout to ensure all refs are populated
    const timer = setTimeout(performInitialScroll, 400);
    return () => clearTimeout(timer);
  }, [messages, modalOpen, fullName, phoneNumber]);

  // Listen for incoming WebSocket messages
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "receiveMessage") {
          setMessages((prevMessages) => {
            // Prevent duplicate messages by checking _id
            const exists = prevMessages.some(msg => 
              msg._id === data.message._id
            );
            if (exists) return prevMessages;
            
            return [...prevMessages, data.message];
          });
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [ws]);

  const handleSendMessage = useCallback(async () => {
    if (!message.trim()) {
      alert("Please enter a message to send.");
      return;
    }

    // Prioritize fullName over phoneNumber - use userData.fullName if available
    const fullNameStr = fullName?.trim() || 
                        userData?.fullName?.trim() || 
                        userData?.name?.trim() || 
                        "User";
    
    // Extract only the first name (first word before space)
    const senderName = fullNameStr.split(' ')[0];
    
    if (!senderName || senderName === "User") {
      alert("Unable to identify user name. Please update your profile.");
      return;
    }

    const newMessage = {
      message: message.trim(),
      sender: senderName,
      timestamp: new Date().toISOString(),
      replyTo: replyToMessage ? replyToMessage._id : null,
    };

    try {
      // Try WebSocket first if connected
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(
          JSON.stringify({
            type: "sendMessage",
            sender: senderName,
            message: newMessage.message,
            replyTo: newMessage.replyTo,
          })
        );

        // This prevents duplicate messages for the sender
        setMessage("");
        setReplyToMessage(null);
      } else {
        // Fallback to API if WebSocket not available
        console.warn("WebSocket not connected, using API fallback");
        
        try {
          const response = await api.post("/chat", {
            sender: senderName,
            message: newMessage.message,
            replyTo: newMessage.replyTo,
          });
          
          setMessages((prevMessages) => [
            ...prevMessages,
            {
              ...response.data,
              _id: response.data._id || `temp-${Date.now()}`,
            },
          ]);

          setMessage("");
          setReplyToMessage(null);
        } catch (apiError) {
          console.error("API error:", apiError);
          const errorMsg = apiError.response?.data?.error || apiError.message || "Failed to send message";
          alert(`${errorMsg}. Please check your connection and try again.`);
        }
      }
    } catch (error) {
      console.error("Error while sending message:", error);
      alert("An error occurred while sending the message.");
    }
  }, [message, replyToMessage, fullName, userData, ws]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleDeleteChat = async () => {
    if (window.confirm("Are you sure you want to delete this message?")) {
      if (!selectedMessage || !selectedMessage._id) {
        console.error("No valid message ID to delete.");
        return;
      }

      try {
        await api.delete(`/messages/${selectedMessage._id}`);
        setMessages((prevMessages) =>
          prevMessages.filter((message) => message._id !== selectedMessage._id)
        );
      } catch (error) {
        console.error("Error deleting message:", error.message);
        alert(error.message || "An error occurred while deleting the message.");
      }
    }
    setMenuVisible(false);
  };

  const handleLongPress = (message, event) => {
    event.preventDefault();
    setSelectedUser(message.sender);
    setSelectedMessage(message);
    setMenuVisible(true);
  };

  const handleCopy = () => {
    if (selectedMessage) {
      navigator.clipboard.writeText(selectedMessage.message);
      alert("Message copied to clipboard.");
    }
    setMenuVisible(false);
  };

  const handleDashMoney = () => {
    setMenuVisible(false);
    setModalVisible(true);
  };

  const dashUser = async () => {
    const amount = parseFloat(dashAmount);
    if (isNaN(amount) || amount <= 0) {
      alert("Please enter a valid amount to dash.");
      return;
    }

    const fullNameStr = fullName?.trim() || userData?.fullName?.trim() || userData?.name?.trim() || "User";
    // Extract only the first name
    const senderName = fullNameStr.split(' ')[0];

    try {
      const response = await api.post(`/dash-user`, {
        sender: senderName,
        receiver: selectedUser,
        amount,
      });

      alert(`You dashed ${amount} to ${selectedUser}`);
    } catch (error) {
      if (error.response) {
        console.error("Server error:", error.response.data);
        alert(error.response.data.message || "Failed to dash user.");
      } else {
        console.error("Error:", error);
        alert("An error occurred while dashing money.");
      }
    }

    setDashAmount("");
    setModalVisible(false);
  };

  const handleReply = (message) => {
    setReplyToMessage(message);
  };

  const findRepliedMessage = useCallback(
    (replyToId) => messages.find((message) => message._id === replyToId),
    [messages]
  );

  const scrollToRepliedMessage = (repliedMessageId) => {
    const element = messageRefs.current[repliedMessageId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('highlight-message');
      setTimeout(() => {
        element.classList.remove('highlight-message');
      }, 2000);
    }
  };

  const renderRepliedMessage = useCallback(
    (repliedMessageId) => {
      const repliedMessage = findRepliedMessage(repliedMessageId);
      if (!repliedMessage) return null;

      return (
        <div
          className="replied-message-container"
          onClick={() => scrollToRepliedMessage(repliedMessage._id)}
        >
          <div className="replied-sender">{getFirstName(repliedMessage.sender)}</div>
          <div className="replied-text">{repliedMessage.message}</div>
        </div>
      );
    },
    [messages, findRepliedMessage, getFirstName]
  );

  return (
    <>
      {/* FAB Button */}
      <div className="userchat-fab" onClick={() => setModalOpen(!modalOpen)} title="User Chat">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          width="28"
          height="28"
        >
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
          <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z"/>
        </svg>
      </div>

      {/* Chat Modal */}
      {modalOpen && (
        <div className="userchat-modal-overlay" onClick={() => setModalOpen(false)}>
          <div 
            className="userchat-modal-content" 
            onClick={(e) => e.stopPropagation()}
            ref={modalContentRef}
          >
            <div className="userchat-header">
              <div className="userchat-header-content">
                <h2>HUNTEXLOTTO GROUP CHAT</h2>
                <div style={{ textAlign: "center", fontSize: "10px" }}>Do not share your personal information.</div>
                {/*<div className={`connection-status ${wsConnected ? 'connected' : 'disconnected'}`}>
                  <span className="status-dot"></span>
                  {wsConnected ? 'Connected' : 'Connecting...'}
                </div>*/}
              </div>
              <button className="userchat-close-btn" onClick={() => setModalOpen(false)}>×</button>
            </div>

            <div className="userchat-messages" ref={messagesContainerRef}>
              {messages.map((item, index) => (
                <React.Fragment key={item._id}>
                  {shouldShowDateSeparator(item, messages[index - 1]) && (
                    <div className="date-separator">
                      <span className="date-separator-text">
                        {formatDateSeparator(item.timestamp)}
                      </span>
                    </div>
                  )}
                  <div
                    ref={(el) => (messageRefs.current[item._id] = el)}
                    className={`userchat-message ${
                      isMyMessage(item.sender)
                        ? "message-sent"
                        : "message-received"
                    }`}
                    onContextMenu={(e) => handleLongPress(item, e)}
                  >
                  <div className="message-avatar">
                    <div className="avatar-circle">
                      {getFirstName(item.sender || "U").charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="message-content">
                    <div className="message-sender">{getFirstName(item.sender)}</div>
                    <div className="message-box">
                      {item.replyTo && renderRepliedMessage(item.replyTo._id || item.replyTo)}
                      <div className="message-text">{item.message}</div>
                      <div className="message-timestamp">
                        {formatTimestamp(item.timestamp)}
                      </div>
                    </div>
                    <div className="message-actions">
                      <button
                        className="reply-btn"
                        onClick={() => handleReply(item)}
                        title="Reply"
                      >
                        ↩
                      </button>
                    </div>
                  </div>
                </div>
                </React.Fragment>
              ))}
              <div ref={chatEndRef} />
            </div>

            {replyToMessage && (
              <div className="reply-bar-container">
                <div className="reply-bar-left">
                  <div className="reply-sender">Replying to {getFirstName(replyToMessage.sender)}</div>
                  <div className="reply-message">{replyToMessage.message}</div>
                </div>
                <button className="reply-cancel" onClick={() => setReplyToMessage(null)}>
                  ×
                </button>
              </div>
            )}

            <div className="userchat-input-container">
              <input
                ref={inputRef}
                type="text"
                className="userchat-input"
                placeholder="Type a message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              />
              <button className="userchat-send-btn" onClick={handleSendMessage}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="24"
                  height="24"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {isMenuVisible && (
        <div className="context-menu-overlay" onClick={() => setMenuVisible(false)}>
          <div className="context-menu" onClick={(e) => e.stopPropagation()}>
            <button className="menu-option" onClick={handleCopy}>
              Copy
            </button>
            {!isMyMessage(selectedUser) && (
              <button className="menu-option" onClick={handleDashMoney}>
                Dash Money
              </button>
            )}
            {isMyMessage(selectedUser) && (
              <button className="menu-option delete-option" onClick={handleDeleteChat}>
                Delete Message
              </button>
            )}
          </div>
        </div>
      )}

      {/* Dash Money Modal */}
      {isModalVisible && (
        <div className="dash-modal-overlay" onClick={() => setModalVisible(false)}>
          <div className="dash-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Gift {selectedUser} an amount</h3>
            <input
              type="number"
              className="dash-input"
              placeholder="Amount (GHC)"
              value={dashAmount}
              onChange={(e) => setDashAmount(e.target.value)}
            />
            <div className="dash-modal-buttons">
              <button className="dash-btn" onClick={dashUser}>
                Dash
              </button>
              <button className="cancel-btn" onClick={() => setModalVisible(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserChat;
